<template>
  <!--  Todo Version Info -->
  <StepsIndicator />

  <!-- TEMPORARY: Service role section hidden for SIA/OAI clients and the friends and family workshop version -->
  <BaseSection v-if="showServiceRoleOptions" id="role" :nextStep="true" @onNextStepClick="projectBase.onNextStepClick">
    <h2 class="text-xl">{{ wt('role', 'title') }}</h2>
    <HelpText slug="project_setup_role" :role="roleUpper"></HelpText>
    <section class="mt-8 boxed-button-grid">
      <label
        v-for="role in serviceRoleOptions"
        :key="role"
        class="base-button"
        :class="{ active: formData?.serviceRole === role }"
        @click.prevent="toggleServiceRole(role)">
        {{ wt('role', 'option', role) }}
        <input type="radio" :value="role" :checked="formData?.serviceRole === role" name="serviceRole" class="hidden" />
      </label>
    </section>
  </BaseSection>

  <BaseSection id="definition" :nextStep="true" @onNextStepClick="projectBase.onNextStepClick">
    <h2 class="text-xl">{{ wt('definition', 'title') }}</h2>
    <HelpText slug="project_setup_definition" :role="roleUpper"></HelpText>
    <SetupDefinitionForm
      v-model="formData"
      :role="roleUpper"
      :setupCalculator="setupCalculator"
      :sharedUsages="sharedUsages"
      :quantityArea="quantityArea" />
  </BaseSection>

  <!-- TEMPORARY: Complexity section gets nextModule button when risk section is hidden -->
  <BaseSection
    id="complexity"
    :nextStep="showRiskSection"
    :nextModule="showRiskSection ? undefined : 'service'"
    @onNextStepClick="projectBase.onNextStepClick"
    @onNextModuleClick="projectBase.onNextModuleClick(partKey, formData, 'service-project-view', undefined, role)">
    <h2 class="text-xl">{{ wt('complexity', 'title') }}</h2>
    <HelpText slug="project_setup_complexity" :role="roleUpper"></HelpText>
    <p class="my-4 text-sm text-neutral-550">
      {{ wt('complexity', 'subtitle') || $t('project.setup.sections.complexity.subtitle') }}
    </p>
    <SetupComplexityForm
      v-model="formData"
      :role="roleUpper"
      :setupCalculator="setupCalculator"
      :sharedUsages="sharedUsages"
      :quantityArea="quantityArea" />
  </BaseSection>

  <!-- TEMPORARY: Risk section hidden for SIA/OAI clients and the friends and family workshop version -->
  <BaseSection
    v-if="showRiskSection"
    id="risk"
    @onNextModuleClick="projectBase.onNextModuleClick(partKey, formData, 'service-project-view', undefined, role)"
    nextModule="service">
    <h2 class="text-xl">{{ wt('risk', 'title') }}</h2>
    <HelpText slug="project_setup_risk" :role="roleUpper"></HelpText>
    <SetupRiskForm v-model="formData" :role="roleUpper" />
  </BaseSection>
</template>

<script setup lang="ts">
import { ref, onMounted, type Ref, watch, computed } from 'vue'
import BaseSection from '@/components/partials/layout/BaseSection.vue'
import StepsIndicator from '@/components/partials/navigation/StepsIndicator.vue'
import HelpText from '@/components/partials/display/HelpText.vue'
import { onBeforeRouteUpdate, onBeforeRouteLeave } from 'vue-router'
import projectPageBase, { type ValidationConfig } from '@/base/projectPageBase'
import type { NestedObject } from '@/types'
import type { SharedUsageItem } from '@/schemas/general/types'
import { deepClone } from '@/helpers/objects_arrays'
import { DEFAULT_QUANTITY_AREA, DEFAULT_COUNTRY } from '@arcexperts/core-types'
import { nonRequiredMaxValue, nonRequiredMinValue } from '@/validation/field-validators'
import { ServiceRoleOptions, countryHasServiceRoleOptions } from '@/schemas/general/enums'
import { useProjectGeneralCalculator } from '@/stores/projectCenter/projectGeneralCalculator'
import { useClientFeatureFlags } from '@/helpers/client-feature-flags'
import { useWidgetText } from '@/helpers/useWidgetText'

// Import shared form components
import SetupDefinitionForm from '@/components/project/shared/SetupDefinitionForm.vue'
import SetupComplexityForm from '@/components/project/shared/SetupComplexityForm.vue'
import SetupRiskForm from '@/components/project/shared/SetupRiskForm.vue'

// Import calculators
import {
  useProjectArchitectCalculator,
  useProjectLandscapeArchitectCalculator,
  useProjectCivilEngineerCalculator,
  useProjectCivilEngineerExcavationCalculator,
  useProjectElectricalCalculator,
  useProjectEngineerCalculator,
  useProjectHeatingCoolingCalculator,
  useProjectVentilationACCalculator,
  useProjectHVACCalculator,
  useProjectSanitaryCalculator,
  useProjectBuildingAutomationCalculator,
  useProjectSpecialistCoordinationCalculator,
} from '@/stores/projectCenter/projectServiceCalculatorFactory'
import type { Role } from '@/stores/projectCenter/useCalculatorForRole'

export type CompanyType =
  | 'architect'
  | 'landscape_architect'
  | 'civil_engineer'
  | 'civil_engineer_excavation'
  | 'electrical'
  | 'engineer'
  | 'heating_cooling'
  | 'ventilation_ac'
  | 'hvac'
  | 'sanitary'
  | 'building_automation'
  | 'specialist_coordination'

// Props
const props = defineProps<{
  companyType: CompanyType
  role?: string
}>()

// Computed values based on company type
const partKey = computed(() => `${props.companyType}_setup`)
const roleByCompanyType: Record<CompanyType, Role> = {
  architect: 'ARCHITECT',
  landscape_architect: 'LANDSCAPE_ARCHITECT',
  civil_engineer: 'CIVIL_ENGINEER',
  civil_engineer_excavation: 'CIVIL_ENGINEER_EXCAVATION',
  electrical: 'ELECTRICAL',
  engineer: 'ENGINEER',
  heating_cooling: 'HEATING_COOLING',
  ventilation_ac: 'VENTILATION_AC',
  hvac: 'HVAC',
  sanitary: 'SANITARY',
  building_automation: 'BUILDING_AUTOMATION',
  specialist_coordination: 'SPECIALIST_COORDINATION',
}

const setupSetterByCompanyType: Record<CompanyType, string> = {
  architect: 'setProjectArchitectSetupData',
  landscape_architect: 'setProjectLandscapeArchitectSetupData',
  civil_engineer: 'setProjectCivilEngineerSetupData',
  civil_engineer_excavation: 'setProjectCivilEngineerExcavationSetupData',
  electrical: 'setProjectElectricalSetupData',
  engineer: 'setProjectEngineerSetupData',
  heating_cooling: 'setProjectHeatingCoolingSetupData',
  ventilation_ac: 'setProjectVentilationACSetupData',
  hvac: 'setProjectHVACSetupData',
  sanitary: 'setProjectSanitarySetupData',
  building_automation: 'setProjectBuildingAutomationSetupData',
  specialist_coordination: 'setProjectSpecialistCoordinationSetupData',
}

const roleUpper = computed<Role>(() => roleByCompanyType[props.companyType])

// Calculator mapping
const calculatorMap = {
  architect: useProjectArchitectCalculator,
  landscape_architect: useProjectLandscapeArchitectCalculator,
  civil_engineer: useProjectCivilEngineerCalculator,
  civil_engineer_excavation: useProjectCivilEngineerExcavationCalculator,
  electrical: useProjectElectricalCalculator,
  engineer: useProjectEngineerCalculator,
  heating_cooling: useProjectHeatingCoolingCalculator,
  ventilation_ac: useProjectVentilationACCalculator,
  hvac: useProjectHVACCalculator,
  sanitary: useProjectSanitaryCalculator,
  building_automation: useProjectBuildingAutomationCalculator,
  specialist_coordination: useProjectSpecialistCoordinationCalculator,
}

// Get the appropriate calculator
const setupCalculator = computed(() => calculatorMap[props.companyType]())

// Validation config for setup pages
const validationConfig: ValidationConfig = {
  schema: {
    constructionCost: nonRequiredMinValue(0),
    buildingCost: nonRequiredMinValue(0),
    'usageCorrections[].manualArea': nonRequiredMinValue(1),
    'usageCorrections[].complexityFactorOverride': [nonRequiredMinValue(0.1), nonRequiredMaxValue(2)],
  },
  fieldSections: {
    constructionCost: '#definition',
    buildingCost: '#definition',
    'usageCorrections[].manualArea': '#definition',
    'usageCorrections[].complexityFactorOverride': '#complexity',
  },
  sectionErrors: {
    '#definition': 'project.setup.sections.definition.error.default',
    '#complexity': 'project.setup.sections.complexity.error.default',
  },
}

const formData: Ref<any> = ref()
const projectBase = projectPageBase.useProjectPageBase(validationConfig)
const projectGeneralCalculator = useProjectGeneralCalculator()

// Get shared usages and quantity area from general.
// SIA 105 usage split: the landscape architect works on landscapeUsageItems/landscapeQuantityArea,
// all other roles on the standard usageItems/quantityArea.
const generalData = computed(() => projectGeneralCalculator.projectGeneral ?? projectBase.project.value?.general?.data)
const isLandscapeArchitect = computed(() => props.companyType === 'landscape_architect')
const sharedUsages = computed<SharedUsageItem[]>(() =>
  isLandscapeArchitect.value
    ? ((generalData.value?.landscapeUsageItems as SharedUsageItem[]) ?? [])
    : ((generalData.value?.usageItems as SharedUsageItem[]) ?? [])
)
const quantityArea = computed<number>(() =>
  isLandscapeArchitect.value
    ? ((generalData.value?.landscapeQuantityArea as number) ?? DEFAULT_QUANTITY_AREA)
    : ((generalData.value?.quantityArea as number) ?? DEFAULT_QUANTITY_AREA)
)

// TEMPORARY: Client feature flags for hiding sections
const { showServiceRoleSection: clientAllowsServiceRole, showRiskSection: clientAllowsRisk } = useClientFeatureFlags()

// Service role options - only for CH
const country = computed(() => (projectGeneralCalculator.projectGeneral?.country as string) || DEFAULT_COUNTRY)
// TEMPORARY: Also check if client allows service role section
const showServiceRoleOptions = computed(
  () => countryHasServiceRoleOptions.includes(country.value) && clientAllowsServiceRole.value
)
const serviceRoleOptions = Object.values(ServiceRoleOptions)
const { wt } = useWidgetText('widget-texts-project-setup', roleUpper)

// TEMPORARY: Risk section visibility
const showRiskSection = computed(() => clientAllowsRisk.value)

// Toggle service role (click same button again to deselect)
function toggleServiceRole(role: ServiceRoleOptions) {
  if (formData.value) {
    if (formData.value.serviceRole === role) {
      formData.value.serviceRole = null
    } else {
      formData.value.serviceRole = role
    }
  }
}

function initForm(): void {
  const project = projectBase.project.value
  const projectPart = project[partKey.value]

  const defaults: NestedObject = {
    constructionCost: 0,
    buildingCost: 0,
    usageCorrections: [],
  }

  const source = (projectPart?.data as Partial<NestedObject>) || {}
  const merged = { ...defaults, ...source } as NestedObject

  // Guard against a non-array usageCorrections coming from backend
  if (!Array.isArray((merged as any).usageCorrections)) {
    ;(merged as any).usageCorrections = []
  }

  formData.value = merged

  // TEMPORARY: Set default risk for clients with hidden risk section
  if (!clientAllowsRisk.value && !formData.value.risk) {
    formData.value.risk = 'LOW_RISK'
  }

  // Initialize calculator
  setupCalculator.value.initSetupData(formData.value as NestedObject)

  projectBase.validator.initForm(formData.value, projectBase.validationSchema)
}

async function onSaveProject(): Promise<void> {
  if (!formData.value) {
    return
  }
  const success = await projectBase.saveProjectPart(partKey.value, formData.value)
  if (success) {
    initForm()
  }
}

function syncCurrentFormToProject(): void {
  if (!formData.value) return
  const projectPart = projectBase.project.value?.[partKey.value]
  if (!projectPart) return
  projectPart.data = deepClone(formData.value)
}

onMounted(() => {
  initForm()
})

watch(
  () => props.companyType,
  () => {
    initForm()
  }
)

onBeforeRouteUpdate(async (to, from, next) => {
  await projectBase.checkFormIsDirty(next, { type: partKey.value, data: formData.value })
})
onBeforeRouteLeave(async (to, from, next) => {
  await projectBase.checkFormIsDirty(next, { type: partKey.value, data: formData.value })
})

watch(
  formData,
  () => {
    projectBase.validator.onFormValuesChange(formData.value as NestedObject)

    // Call the appropriate calculator method based on company type
    const methodName = setupSetterByCompanyType[props.companyType]
    const calculator = projectBase.calculator as any
    if (calculator[methodName]) {
      calculator[methodName](formData.value)
    }
  },
  { deep: true, immediate: false }
)

defineExpose({
  onSaveProject,
  syncCurrentFormToProject,
})
</script>
